import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, ClipboardList, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function HousekeepingTab({ hotelId }) {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);

  useEffect(() => {
    loadHousekeepingTasks();
  }, [hotelId]);

  const loadHousekeepingTasks = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/pms/${hotelId}/housekeeping`);
      const data = await response.json();
      if (data.success) {
        setTasks(data.tasks || []);
      }
    } catch (error) {
      console.error('Failed to load housekeeping tasks:', error);
      toast.error('Failed to load housekeeping tasks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <Skeleton className="h-12 w-12 rounded-full mx-auto mb-4" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </div>
    );
  }

  let filteredTasks = tasks;
  if (statusFilter) {
    filteredTasks = tasks.filter(t => t.status === statusFilter);
  }

  const urgentTasks = filteredTasks.filter(t => t.priority === 'urgent');
  const highTasks = filteredTasks.filter(t => t.priority === 'high');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');

  return (
    <div className="space-y-6" data-testid="housekeeping-tab">
      <div>
        <h2 className="text-2xl font-bold heading-font mb-2">Housekeeping</h2>
        <p className="text-muted-foreground">Room cleaning and maintenance tasks</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-destructive/20" onClick={() => setStatusFilter('pending')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-destructive">{urgentTasks.length}</p>
            <p className="text-sm text-muted-foreground">Urgent Tasks</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow border-primary/20" onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{highTasks.length}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter(null)}>
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[hsl(var(--status-clean))]">{completedTasks.length}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-2 flex-wrap">
        <Badge variant={statusFilter === null ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setStatusFilter(null)}>
          All ({tasks.length})
        </Badge>
        <Badge variant={statusFilter === 'pending' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setStatusFilter('pending')}>
          Pending
        </Badge>
        <Badge variant={statusFilter === 'in_progress' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setStatusFilter('in_progress')}>
          In Progress
        </Badge>
        <Badge variant={statusFilter === 'completed' ? 'default' : 'outline'} className="cursor-pointer" onClick={() => setStatusFilter('completed')}>
          Completed
        </Badge>
      </div>

      {filteredTasks.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold mb-2">No Tasks</h3>
            <p className="text-sm text-muted-foreground">
              {statusFilter ? `No ${statusFilter} tasks` : 'All rooms are clean!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTasks.map(task => (
            <Card key={task.id} className="hover:shadow-md transition-shadow" data-testid={`housekeeping-task-${task.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">Room {task.room_number}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{task.task_type}</p>
                  </div>
                  <Badge variant={task.priority === 'urgent' ? 'destructive' : task.priority === 'high' ? 'warning' : 'outline'}>
                    {task.priority}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={task.status === 'completed' ? 'default' : 'outline'}>
                    {task.status}
                  </Badge>
                </div>
                {task.assigned_to && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Assigned to</span>
                    <span className="font-medium">{task.assigned_to}</span>
                  </div>
                )}
                {task.notes && (
                  <p className="text-xs text-muted-foreground mt-2 p-2 bg-muted rounded">{task.notes}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
